import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import { useTenantTranslation } from "hooks/formatters";
import { EventType, MaterialRead } from "src/store/api/generatedApi";
import { useMaterialColor } from "hooks/useMaterials";
import { ScrapEyesEvent } from "components/scrapEyeLabelling/types";
import { AreaSelectionModal } from "components/scrapEyeLabelling/modals/AreaSelectionModal";
import { AreaChip } from "components/manualReview/components/AreaChip";
import { useAreaLabel } from "components/scrapEyeLabelling/hooks/useAreaLabel";
import { Dayjs } from "dayjs";
import { getLabellingMaterialDisplayName } from "components/scrapEyeLabelling/utils/materialDisplayName";

export const ScrapEyesEventEdit = ({
  event,
  materialOptions,
  onUpdate,
  getAreaMaterialMappingAtTime,
  location,
}: {
  event: ScrapEyesEvent;
  materialOptions: MaterialRead[];
  onUpdate: (updates: {
    comment?: string;
    material: string | null;
    area?: string | null;
  }) => void;
  getAreaMaterialMappingAtTime?: (timestamp: Dayjs) => Record<string, string>;
  location: string;
}) => {
  const [open, setOpen] = useState(false);

  const handleCommentButtonClick = () => {
    setOpen(true);
  };
  const handleCommentDialogClose = () => {
    setOpen(false);
  };

  const handleConfirm = (updates: {
    comment?: string;
    material: string | null;
    area?: string | null;
  }) => {
    onUpdate(updates);
    handleCommentDialogClose();
  };

  return (
    <>
      <IconButton
        onClick={handleCommentButtonClick}
        data-testid={event.comment ? "edit-button-with-comment" : "edit-button"}
        size="small"
        sx={{
          backgroundColor: (theme) =>
            event.comment ? theme.palette.warning.light : null,
          "&:hover": {
            backgroundColor: (theme) =>
              event.comment ? theme.palette.warning.main : null,
          },
        }}
      >
        <EditRoundedIcon sx={{ fontSize: 16 }} />
      </IconButton>

      <Dialog open={open} onClose={handleCommentDialogClose}>
        <EventEditDialogContent
          eventType={event.eventType}
          onClose={handleCommentDialogClose}
          material={event.material}
          area={event.area}
          comment={event.comment}
          onConfirm={handleConfirm}
          materialOptions={materialOptions}
          eventTimestamp={event.start}
          getAreaMaterialMappingAtTime={getAreaMaterialMappingAtTime}
          location={location}
        />
      </Dialog>
    </>
  );
};

const EventEditDialogContent = ({
  onClose,
  material,
  area,
  comment,
  eventType,
  onConfirm,
  materialOptions,
  eventTimestamp,
  getAreaMaterialMappingAtTime,
  location,
}: {
  onClose: () => void;
  material: string | null;
  area: string | null;
  comment?: string;
  eventType: EventType;
  onConfirm: (updates: {
    comment?: string;
    material: string | null;
    area?: string | null;
  }) => void;
  materialOptions: MaterialRead[];
  eventTimestamp: Dayjs;
  getAreaMaterialMappingAtTime?: (timestamp: Dayjs) => Record<string, string>;
  location: string;
}) => {
  const { t } = useTenantTranslation();
  const { getColor } = useMaterialColor();
  const resolveAreaLabel = useAreaLabel(location);
  const [newComment, setNewComment] = useState(comment);
  const [newMaterial, setNewMaterial] = useState(material ?? "unknown");
  const [newArea, setNewArea] = useState(area ?? null);
  const [areaModalOpen, setAreaModalOpen] = useState(false);

  const areaColors = useMemo((): Record<string, string> | undefined => {
    if (!getAreaMaterialMappingAtTime) return undefined;

    const mapping = getAreaMaterialMappingAtTime(eventTimestamp);
    const colors: Record<string, string> = {};

    for (const [areaKey, materialName] of Object.entries(mapping)) {
      const color = getColor(materialName);
      if (color) {
        colors[areaKey] = color;
      }
    }

    return colors;
  }, [getAreaMaterialMappingAtTime, eventTimestamp, getColor]);

  const handleConfirm = () => {
    if (eventType === "flag") {
      onConfirm({
        comment: newComment,
        material: null,
        area: null,
      });
    } else {
      onConfirm({
        comment: newComment,
        material: newMaterial === "unknown" ? null : newMaterial,
        area: newArea,
      });
    }
  };

  return (
    <>
      <DialogContent sx={{ minWidth: 350 }}>
        <Stack gap={2}>
          <TextField
            autoFocus
            value={newComment}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setNewComment(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                handleConfirm();
              }
            }}
            label={t("comment")}
            fullWidth
            variant="standard"
          />
          {eventType !== "flag" ? (
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="body2" color="text.secondary">
                {t("location")}:
              </Typography>
              <AreaChip
                selected={false}
                area={newArea ?? ""}
                label={
                  newArea === null ? t("selectArea") : resolveAreaLabel(newArea)
                }
                onClick={() => {
                  setAreaModalOpen(true);
                }}
              />
            </Stack>
          ) : null}
          {eventType !== "flag" ? (
            <FormControl variant="standard">
              <InputLabel id="material-select-label">
                {t("material")}
              </InputLabel>
              <Select
                labelId="material-select-label"
                value={newMaterial}
                onChange={(event) => {
                  setNewMaterial(event.target.value);
                }}
              >
                <MenuItem value={"unknown"}>
                  <Box sx={{ pr: 2, flex: 1 }}>
                    <MaterialLabel material={null} />
                  </Box>
                </MenuItem>
                {materialOptions.map((material) => (
                  <MenuItem
                    key={`${material.name}-label`}
                    value={material.name}
                  >
                    <Box sx={{ pr: 2, flex: 1 }}>
                      <MaterialLabel material={material} />
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          color="secondary"
          variant="outlined"
          onClick={onClose}
          startIcon={<CloseIcon sx={{ fontSize: 14 }} />}
          size="small"
        >
          {t("cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          startIcon={<EditRoundedIcon />}
          size="small"
        >
          {t("confirm")}
        </Button>
      </DialogActions>
      {eventType !== "flag" ? (
        <AreaSelectionModal
          open={areaModalOpen}
          onClose={() => {
            setAreaModalOpen(false);
          }}
          onSelectArea={(area: string) => {
            setNewArea(area);
          }}
          eventType={eventType}
          areaColors={areaColors}
          location={location}
        />
      ) : null}
    </>
  );
};

export const MaterialLabel = ({
  material,
}: {
  material: MaterialRead | null;
}) => {
  const { t } = useTenantTranslation();
  const { getColor } = useMaterialColor();
  const materialDisplayName = getLabellingMaterialDisplayName(
    material,
    t
  ).toUpperCase();
  return (
    <Box
      data-testid="material-label"
      sx={{
        width: "100%",
        px: 1,
        borderRadius: 1,
        py: 0.5,
        backgroundColor: getColor(material?.name ?? ""),
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: "white",
          fontWeight: "bold",
        }}
      >
        {materialDisplayName}
      </Typography>
    </Box>
  );
};
